// FatturaElettronicaPDF.js
import React, { useEffect, useMemo, useState } from "react";
import { Page, Text, View, Document, PDFViewer, pdf, StyleSheet } from "@react-pdf/renderer";

/* =========================
   UTIL: decodifica base64 -> UTF-8 senza atob
   ========================= */
async function base64ToUtf8(b64) {
  const s = normalizeB64(b64);
  const res = await fetch(`data:application/octet-stream;base64,${s}`);
  const buf = await res.arrayBuffer();
  return new TextDecoder("utf-8").decode(buf);
}
function normalizeB64(b64) {
  let s = String(b64 || "").replace(/-/g, "+").replace(/_/g, "/");
  const pad = s.length % 4;
  if (pad) s += "=".repeat(4 - pad);
  return s;
}

function breakableIban(iban = "") {
  const clean = String(iban).replace(/\s+/g, "");
  // Inserisce un carattere U+200B ogni 4 char: IT60X0542811101000000123456 -> IT60​X054​2811​1010​0000​1234​56
  return clean.replace(/(.{4})/g, "$1 ").trim();
}

/* =========================
   MAPPE DECODIFICA CODICI
   ========================= */
const TD_MAP = {
  TD01: "Fattura", TD02: "Acconto/Anticipo su fattura", TD03: "Acconto/Anticipo su parcella",
  TD04: "Nota di credito", TD05: "Nota di debito", TD06: "Parcella", TD16: "Integrazione (reverse interno)",
  TD17: "Integrazione servizi estero", TD18: "Integrazione beni intra-UE", TD19: "Integrazione art.17 c.2",
  TD20: "Autofattura regolarizzazione/integrazione", TD21: "Autofattura splafonamento",
  TD22: "Estrazione da deposito IVA", TD23: "Estrazione con versamento IVA",
  TD24: "Fattura differita", TD25: "Autoconsumo/Cessioni gratuite", TD26: "Beni ammortizzabili / passaggi interni",
  TD27: "Autoconsumo/Cessioni gratuite (senza IVA)", TD28: "Acquisti da San Marino (con IVA)",
};
const NATURA_MAP = {
  N1: "Escluse art. 15", N2: "Non soggette",
  "N2.1": "Non soggette art.7-7septies", "N2.2": "Non soggette (altri casi)",
  N3: "Non imponibili", "N3.1": "Esportazioni", "N3.2": "Cessioni intra-UE", "N3.3": "Cessioni San Marino",
  "N3.4": "Assimilate a esportazioni", "N3.5": "Dichiarazioni d’intento", "N3.6": "Altre non imponibili",
  N4: "Esenti", N5: "Regime del margine", N6: "Reverse charge",
  "N6.1": "RC subappalto edilizia", "N6.2": "RC oro/argento", "N6.3": "RC rottami",
  "N6.4": "RC fabbricati", "N6.5": "RC telefonia", "N6.6": "RC prodotti elettronici",
  "N6.7": "RC edilizia (pulizie/installazioni)", "N6.8": "RC gas/energia", "N6.9": "RC altri casi",
  N7: "IVA assolta in altro Stato UE",
};
const ESIG_MAP = { I: "Immediata", D: "Differita", S: "Split payment" };
const MP_MAP = {
  MP01: "Contanti", MP02: "Assegno", MP03: "Assegno circolare", MP04: "Tesoreria",
  MP05: "Bonifico", MP06: "Vaglia cambiario", MP07: "Bollettino bancario", MP08: "Carta",
  MP09: "RID/SDD", MP10: "RID/SDD veloce", MP11: "RID/SDD ordinario", MP12: "Ri.Ba.",
  MP13: "MAV", MP14: "Quietanza erario", MP15: "Giroconto Tesoreria", MP16: "Domiciliazione bancaria",
  MP17: "Domiciliazione postale", MP18: "Bollettino postale", MP19: "SEPA Direct Debit",
  MP20: "SEPA Credit Transfer", MP21: "PagoPA",
};
const RF_MAP = {
  RF01: "Ordinario", RF02: "Minimi", RF04: "Agricoltura", RF05: "Sali e tabacchi", RF06: "Fiammiferi",
  RF07: "Editoria", RF08: "Telefonia pubblica", RF09: "Documenti trasporto/sosta",
  RF10: "Agenzie viaggi", RF11: "Agriturismo", RF12: "Vendite a domicilio",
  RF13: "Margine beni usati/arte", RF14: "Onoranze funebri", RF15: "Intrattenimenti",
  RF16: "IVA per cassa", RF17: "Subfornitura", RF18: "Altro", RF19: "Forfetario",
};
const EMITTENTE_MAP = { CC: "Cessionario/Committente", TZ: "Terzo (intermediario)" };
const decode = (map, code) => (code ? map[code] || code : "");
const decodeNatura = (code) => (code ? NATURA_MAP[code] || NATURA_MAP[code.split(".")[0]] || code : "");

/* =========================
   FORMATTATORI
   ========================= */
const fmt = (n, d = 2) =>
  new Intl.NumberFormat("it-IT", { minimumFractionDigits: d, maximumFractionDigits: d }).format(Number(n) || 0);
const fmtEuro = (n) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(n) || 0);
const fmtDataIt = (ymd) => {
  const s = (ymd || "").slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return ymd || "";
  const [, y, mm, dd] = m;
  return `${Number(dd)}/${Number(mm)}/${y}`;
};

/* =========================
   PARSER XML (namespace-safe)
   ========================= */
const byName = (node, localName) =>
  Array.from((node || document).getElementsByTagName("*")).filter((el) => el.localName === localName);
const firstByName = (node, localName) => (node ? byName(node, localName)[0] : null);
const firstText = (node, localName, fb = "") => {
  const el = firstByName(node, localName);
  return el ? (el.textContent || "").trim() : fb;
};
const toNumber = (s, def = 0) => {
  if (s == null || s === "") return def;
  const v = Number(String(s).replace(",", "."));
  return isNaN(v) ? def : v;
};
const parseAnagrafica = (scope) => {
  if (!scope) return {};
  const dati = firstByName(scope, "DatiAnagrafici") || {};
  const an = firstByName(dati, "Anagrafica") || {};
  const idIva = firstByName(dati, "IdFiscaleIVA") || null;
  const piva = idIva
    ? [firstText(idIva, "IdPaese"), firstText(idIva, "IdCodice")].filter(Boolean).join("")
    : "";
  return {
    denominazione: firstText(an, "Denominazione"),
    nome: firstText(an, "Nome"),
    cognome: firstText(an, "Cognome"),
    cf: firstText(dati, "CodiceFiscale"),
    piva,
    regimeFiscale: firstText(dati, "RegimeFiscale"),
  };
};
const parseIndirizzo = (scopeName, parent) => {
  const n = firstByName(parent || {}, scopeName);
  if (!n) return {};
  return {
    indirizzo: firstText(n, "Indirizzo"),
    numeroCivico: firstText(n, "NumeroCivico"),
    cap: firstText(n, "CAP"),
    comune: firstText(n, "Comune"),
    provincia: firstText(n, "Provincia"),
    nazione: firstText(n, "Nazione"),
  };
};
const displayDenom = (x) =>
  [x?.denominazione, [x?.nome, x?.cognome].filter(Boolean).join(" ")].filter(Boolean).join(" ");

/* =========================
   STILI REACT-PDF
   ========================= */
const styles = StyleSheet.create({
  page: { padding: 28, fontSize: 10, color: "#111" },
  h1: { fontSize: 16, fontWeight: 700 },
  sub: { fontSize: 10, color: "#666", marginTop: 4 },
  row: { flexDirection: "row" },
  col: { flexDirection: "column" },
  spaceBetween: { justifyContent: "space-between" },
  card: { borderWidth: 1, borderColor: "#ddd", borderRadius: 6, padding: 10, backgroundColor: "#fcfcfc" },
  cardTitle: { fontSize: 9, color: "#666", textTransform: "uppercase", marginBottom: 4, fontWeight: 700 },
  partyName: { fontSize: 12, fontWeight: 700 },
  small: { fontSize: 9, color: "#555" },
  chipRow: { marginTop: 6, flexDirection: "row", flexWrap: "wrap", gap: 6 },
  chip: { borderWidth: 1, borderColor: "#ddd", borderRadius: 999, paddingVertical: 2, paddingHorizontal: 6, fontSize: 9 },
  section: { marginTop: 12 },
  table: { width: "100%", borderWidth: 1, borderColor: "#ddd", borderRadius: 4 },
  thRow: { flexDirection: "row", backgroundColor: "#f3f3f3" },
  tdRow: { flexDirection: "row", borderTopWidth: 1, borderColor: "#ddd" },
  cell: { padding: 6, borderRightWidth: 1, borderColor: "#eee", justifyContent: "center" },
  cellLast: { padding: 6, justifyContent: "center" },
  right: { textAlign: "right" },
  bold: { fontWeight: 700 },
  totalsBox: { borderWidth: 1, borderColor: "#ddd", borderRadius: 6, padding: 10 },
  totalLine: { flexDirection: "row", justifyContent: "space-between", marginVertical: 2 },
  totalBig: { fontSize: 14, fontWeight: 700 },
});

/* =========================
   COMPONENTE DOCUMENT
   ========================= */
export function FatturaElettronicaPDFDocument({ dati, notaInFondo }) {
  const d = dati; // alias

  // colonne dei dettagli linee
  const COLS = [
    { key: "num",     label: "#",           flex: 0.06, align: "left"  },
    { key: "desc",    label: "Descrizione", flex: 0.57, align: "left"  },
    { key: "prezzo",  label: "Prezzo",      flex: 0.13, align: "right" },
    { key: "aliq",    label: "Aliq./Nat.",  flex: 0.10, align: "right" },
    { key: "totale",  label: "Totale",      flex: 0.14, align: "right" }, // somma ≈ 1
  ];

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* TITOLO */}
        <View>
          <Text style={styles.h1}>
            {(d.header.tipoDocumentoLabel || "Documento") +
              (d.header.numero ? ` n. ${d.header.numero}` : "") +
              (d.header.dataIt ? ` del ${d.header.dataIt}` : "")}
          </Text>
          <Text style={styles.sub}>            
            {d.soggettoEmittenteLabel ? `Emittente: ${d.soggettoEmittenteLabel}` : ""}
            {d.header.causale ? (d.soggettoEmittenteLabel ? " • " : "") + d.header.causale : ""}
          </Text>
        </View>

        {/* PARTI */}
        <View style={[styles.section, styles.row, styles.spaceBetween, { gap: 10 }]}>
          <View style={[styles.card, { flex: 1 }]}>
            <Text style={styles.cardTitle}>Cedente / Prestatore</Text>
            <Text style={styles.partyName}>{displayDenom(d.cedente) || "—"}</Text>
            <Text style={styles.small}>
              {d.cedente.piva ? `P.IVA ${d.cedente.piva}` : ""}{d.cedente.piva && d.cedente.cf ? " • " : ""}{d.cedente.cf ? `CF ${d.cedente.cf}` : ""}
              {d.cedente.regimeFiscaleLabel ? ` • Regime ${d.cedente.regimeFiscaleLabel}` : (d.cedente.regimeFiscale ? ` • Regime ${d.cedente.regimeFiscale}` : "")}
            </Text>
            <Text style={styles.small}>
              {[d.cedente.sede?.indirizzo, d.cedente.sede?.numeroCivico].filter(Boolean).join(" ")}
            </Text>
            <Text style={styles.small}>
              {[d.cedente.sede?.cap, d.cedente.sede?.comune, d.cedente.sede?.provincia].filter(Boolean).join(" ")}
              {d.cedente.sede?.nazione ? ` (${d.cedente.sede.nazione})` : ""}
            </Text>
          </View>

          <View style={[styles.card, { flex: 1 }]}>
            <Text style={styles.cardTitle}>Cessionario / Committente</Text>
            <Text style={styles.partyName}>{displayDenom(d.cessionario) || "—"}</Text>
            <Text style={styles.small}>
              {d.cessionario.piva ? `P.IVA ${d.cessionario.piva}` : ""}{d.cessionario.piva && d.cessionario.cf ? " • " : ""}{d.cessionario.cf ? `CF ${d.cessionario.cf}` : ""}
            </Text>
            <Text style={styles.small}>
              {[d.cessionario.sede?.indirizzo, d.cessionario.sede?.numeroCivico].filter(Boolean).join(" ")}
            </Text>
            <Text style={styles.small}>
              {[d.cessionario.sede?.cap, d.cessionario.sede?.comune, d.cessionario.sede?.provincia].filter(Boolean).join(" ")}
              {d.cessionario.sede?.nazione ? ` (${d.cessionario.sede.nazione})` : ""}
            </Text>
          </View>
        </View>

        {/* CHIP FISCALI */}
        <View style={styles.chipRow}>
          {d.bollo ? <Text style={styles.chip}>Bollo: {(d.bollo.bolloVirtuale === "SI" ? "Virtuale " : "") + fmtEuro(d.bollo.importo)}</Text> : null}
          {d.ritenuta ? (
            <Text style={styles.chip}>
              Ritenuta: {(d.ritenuta.tipo || "—") + (Number.isFinite(d.ritenuta.aliquota) ? ` ${fmt(d.ritenuta.aliquota,0)}%` : "")} • {fmtEuro(d.ritenuta.importo)}
            </Text>
          ) : null}

          {/* Arrotondamento: non lo metto anche qui sopra, tanto c'è nel riepilogo
          {Number.isFinite(d.header.arrotondamento) && d.header.arrotondamento !== 0 ? (
            <Text style={styles.chip}>Arrotondamento: {(d.header.arrotondamento > 0 ? "+" : "") + fmtEuro(d.header.arrotondamento)}</Text>
          ) : null}
          */}
        </View>

        {/* TABELLA RIGHE */}
        <View style={[styles.section, styles.table]}>
          
          {/* versione iniziale con tutte le colonne, da usare eventualmente per le passive
          <View style={styles.thRow}>
            {["#", "Descrizione", "Codice", "Q.tà", "UM", "Prezzo", "Sconto", "Aliq./Nat.", "Rit.", "Totale"].map((h, i, arr) => (
              <View key={h} style={[styles.cell, i === arr.length - 1 ? styles.cellLast : null, { flexBasis: [30, 180, 80, 45, 40, 60, 60, 90, 35, 70][i], flexGrow: 0 }]}>
                <Text style={[styles.bold, i >= 3 ? styles.right : null]}>{h}</Text>
              </View>
            ))}
          </View>
          
          {d.righe.length === 0 ? (
            <View style={styles.tdRow}>
              <View style={[styles.cellLast, { flex: 1 }]}><Text>Nessuna riga</Text></View>
            </View>
          ) : d.righe.map((r, idx) => (
            <View key={(r.numeroLinea || "") + "-" + idx} style={styles.tdRow}>
                <View style={[styles.cell, { flex: 0.06 }]}><Text>{r.numeroLinea || "—"}</Text></View>
                <View style={[styles.cell, { flex: 0.28 }]}><Text>{r.descrizione || "—"}</Text></View>
                <View style={[styles.cell, { flex: 0.13 }]}><Text>{r.codiceArticolo || "—"}</Text></View>
                <View style={[styles.cell, { flex: 0.08 }]}><Text style={styles.right}>{r.quantita || "—"}</Text></View>
                <View style={[styles.cell, { flex: 0.07 }]}><Text>{r.unitaMisura || "—"}</Text></View>
                <View style={[styles.cell, { flex: 0.10 }]}><Text style={styles.right}>{fmtEuro(r.prezzoUnitario)}</Text></View>
                <View style={[styles.cell, { flex: 0.10 }]}><Text style={styles.right}>{r.sconto ? fmtEuro(r.sconto) : "—"}</Text></View>
                <View style={[styles.cell, { flex: 0.10 }]}><Text style={styles.right}>{Number.isFinite(r.aliquota) ? `${fmt(r.aliquota,0)}%` : (r.naturaLabel || r.natura || "—")}</Text></View>
                <View style={[styles.cell, { flex: 0.05 }]}><Text style={styles.right}>{r.ritenutaSi ? "SI" : "—"}</Text></View>
                <View style={[styles.cellLast, { flex: 0.13 }]}><Text style={styles.right}>{fmtEuro(r.prezzoTotale)}</Text></View>
            </View>            
          ))}
            */}

          {/* versione con le colonne per le attive */}
            <View style={styles.thRow}>
            {COLS.map((c, i) => (
                <View
                key={c.key}
                style={[
                    styles.cell,
                    i === COLS.length - 1 ? styles.cellLast : null,
                    { flex: c.flex }            // <-- qui la magia: niente flexBasis!
                ]}
                >
                <Text style={[styles.bold, c.align === "right" ? styles.right : null]}>
                    {c.label}
                </Text>
                </View>
            ))}
            </View>


            {d.righe.length === 0 ? (
            <View style={styles.tdRow}>
                <View style={[styles.cellLast, { flex: 1 }]}><Text>Nessuna riga</Text></View>
            </View>
            ) : d.righe.map((r, idx) => (
            <View key={(r.numeroLinea || "") + "-" + idx} style={styles.tdRow}>
                <View style={[styles.cell, { flex: COLS[0].flex }]}>
                <Text>{r.numeroLinea || "—"}</Text>
                </View>
                <View style={[styles.cell, { flex: COLS[1].flex }]}>
                <Text>{r.descrizione || "—"}</Text>
                </View>
                <View style={[styles.cell, { flex: COLS[2].flex }]}>
                <Text style={styles.right}>{fmtEuro(r.prezzoUnitario)}</Text>
                </View>
                <View style={[styles.cell, { flex: COLS[3].flex }]}>
                <Text style={styles.right}>
                    {Number.isFinite(r.aliquota) ? `${fmt(r.aliquota,0)}%` : (r.naturaLabel || r.natura || "—")}
                </Text>
                </View>
                <View style={[styles.cellLast, { flex: COLS[4].flex }]}>
                <Text style={styles.right}>{fmtEuro(r.prezzoTotale)}</Text>
                </View>
            </View>
            ))}
            

        </View>

        {/* RIEPILOGO & TOTALI */}
        <View style={[styles.section, styles.row, styles.spaceBetween, { gap: 10 }]}>
          {/* Riepilogo IVA */}
          <View style={{ flex: 1 }}>
            <Text style={[styles.cardTitle, { marginBottom: 6 }]}>Riepilogo IVA</Text>
            <View style={[styles.table]}>
              <View style={styles.thRow}>
                {["Aliq.", "Natura", "Imponibile", "Imposta", "Esig."].map((h, i, arr) => (
                  <View key={h} style={[styles.cell, i === arr.length - 1 ? styles.cellLast : null, { flex: [50, 100, 80, 80, 70][i] }, i===2||i===3 ? styles.right : null]}>
                    <Text style={styles.bold}>{h}</Text>
                  </View>
                ))}
              </View>
              {d.riepiloghi.length === 0 ? (
                <View style={styles.tdRow}><View style={[styles.cellLast, { flex: 1 }]}><Text>Nessun riepilogo</Text></View></View>
              ) : d.riepiloghi.map((x, i) => (
                <View key={i} style={styles.tdRow}>
                  <View style={[styles.cell, { flex: 50 }]}><Text>{Number.isFinite(x.aliquota) ? `${fmt(x.aliquota,0)}%` : "—"}</Text></View>
                  <View style={[styles.cell, { flex: 100 }]}><Text>{x.naturaLabel || x.natura || "—"}</Text></View>
                  <View style={[styles.cell, { flex: 80 }]}><Text style={styles.right}>{fmtEuro(x.imponibile)}</Text></View>
                  <View style={[styles.cell, { flex: 80 }]}><Text style={styles.right}>{fmtEuro(x.imposta)}</Text></View>
                  <View style={[styles.cellLast, { flex: 70 }]}><Text>{x.esigibilitaLabel || x.esigibilita || "—"}</Text></View>
                </View>
              ))}
            </View>
          </View>

          {/* Totali */}
          <View style={{ width: 220 }}>
            <Text style={[styles.cardTitle, { marginBottom: 6 }]}>Totali</Text>
            <View style={styles.totalsBox}>
              <View style={styles.totalLine}>
                <Text>Imponibile</Text>
                <Text>{fmtEuro(d.riepiloghi.reduce((s, x) => s + (x.imponibile || 0), 0))}</Text>
              </View>
              <View style={styles.totalLine}>
                <Text>Imposta</Text>
                <Text>{fmtEuro(d.riepiloghi.reduce((s, x) => s + (x.imposta || 0), 0))}</Text>
              </View>
              {d.bollo ? (
                <View style={styles.totalLine}>
                  <Text>Imposta di bollo</Text>
                  <Text>{fmtEuro(d.bollo.importo)}</Text>
                </View>
              ) : null}
              {Number.isFinite(d.header.arrotondamento) && d.header.arrotondamento !== 0 ? (
                <View style={styles.totalLine}>
                  <Text>Arrotondamento</Text>
                  <Text>{fmtEuro(d.header.arrotondamento)}</Text>
                </View>
              ) : null}
              {d.ritenuta ? (
                <View style={styles.totalLine}>
                  <Text>Ritenuta</Text>
                  <Text>- {fmtEuro(d.ritenuta.importo)}</Text>
                </View>
              ) : null}
              <View style={{ borderTopWidth: 1, borderColor: "#ddd", marginVertical: 6 }} />
              <View style={styles.totalLine}>
                <Text style={styles.bold}>Totale documento</Text>
                <Text style={styles.totalBig}>{fmtEuro(d.header.totaleDocumento)}</Text>
              </View>
              {d.header.divisa && d.header.divisa !== "EUR" ? (
                <View style={styles.totalLine}><Text>Valuta</Text><Text>{d.header.divisa}</Text></View>
              ) : null}
            </View>
          </View>
        </View>

        {/* PAGAMENTI */}
        {d.pagamentiBlocchi.length > 0 ? (
          <View style={styles.section}>
            <Text style={styles.cardTitle}>Pagamenti</Text>
            {d.pagamentiBlocchi.map((pb, idx) => (
              <View key={idx} style={[styles.table, { marginTop: 4 }]}>
                <View style={styles.thRow}>
                  {["Modalità", "Scad.", "Importo", "IBAN", "Istituto", "Beneficiario", "Cod. Pag."].map((h, i, arr) => (
                    <View key={h} style={[styles.cell, i === arr.length - 1 ? styles.cellLast : null, { flex: [50, 60, 60, 120, 80, 120, 40][i] }]}>
                      <Text style={styles.bold}>{h}</Text>
                    </View>
                  ))}
                </View>
                {pb.dettagli.length === 0 ? (
                  <View style={styles.tdRow}><View style={[styles.cellLast, { flex: 1 }]}><Text>Nessun dettaglio pagamento</Text></View></View>
                ) : pb.dettagli.map((dett, i) => (
                  <View key={i} style={styles.tdRow}>
                    <View style={[styles.cell, { flex: 50 }]}><Text>{dett.modalitaLabel || dett.modalita || "—"}</Text></View>
                    <View style={[styles.cell, { flex: 60 }]}><Text>{dett.dataScadenza ? fmtDataIt(dett.dataScadenza) : "—"}</Text></View>
                    <View style={[styles.cell, { flex: 60 }]}><Text style={styles.right}>{fmtEuro(dett.importo)}</Text></View>
                    <View style={[styles.cell, { flex: 120 }]}><Text>{dett.iban ? breakableIban(dett.iban) : "—"}</Text></View>
                    <View style={[styles.cell, { flex: 80 }]}><Text>{dett.istituto || "—"}</Text></View>
                    <View style={[styles.cell, { flex: 120 }]}><Text>{dett.beneficiario || "—"}</Text></View>
                    <View style={[styles.cellLast, { flex: 40 }]}><Text>{dett.codicePagamento || "—"}</Text></View>
                  </View>
                ))}
              </View>
            ))}
          </View>
        ) : null}
        {notaInFondo!="" && <View style={{marginTop:20}}><Text>{notaInFondo}</Text></View>}
      </Page>
    </Document>
  );
}

/* =========================
   WRAPPER CON VIEWER & DOWNLOAD
   ========================= */
export default function FatturaElettronicaPDF({
  xmlString = "",
  xmlBase64 = "",
  height = 600,         // altezza del viewer
  notaInFondo=""
}) {
  const [xml, setXml] = useState(xmlString);

  // decodifica base64 se fornito
  useEffect(() => {
    let abort = false;
    (async () => {
      if (xmlBase64) {
        const txt = await base64ToUtf8(xmlBase64);
        if (!abort) setXml(txt);
      } else {
        setXml(xmlString || "");
      }
    })();
    return () => { abort = true; };
  }, [xmlString, xmlBase64]);

  // parse + model
  const dati = useMemo(() => (xml ? parseFattura(xml) : null), [xml]);

  // funzione download PDF
  async function handleDownloadPDF() {
    if (!dati) return;
    const blob = await pdf(<FatturaElettronicaPDFDocument dati={dati} />).toBlob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const fileNamePdf = `${dati.idPaeseTrasmittente}${dati.idCodiceTrasmittente}_${dati.progressivoInvio}.pdf`; 
    a.download = fileNamePdf;
    document.body.appendChild(a); a.click();
    a.remove(); URL.revokeObjectURL(url);
  }

  // funzione download XML
  async function handleDownloadXML() {
    if (!dati) return;
    if (!xml) return;

    // opzionale: aggiungi la dichiarazione XML se assente
    let xmlOut = xml.trim();
    if (!xmlOut.startsWith("<?xml")) {
      xmlOut = `<?xml version="1.0" encoding="UTF-8"?>\n` + xmlOut;
    }

    const fileNameXml = `${dati.idPaeseTrasmittente}${dati.idCodiceTrasmittente}_${dati.progressivoInvio}.xml`;

    const blob = new Blob([xmlOut], { type: "application/xml;charset=UTF-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");

    /*
    const safeNumero = (dati?.header?.numero || "documento").toString().replace(/[^\w.-]+/g, "_");
    const safeData   = (dati?.header?.data || "").toString();
    const fileNameXml = `fattura_${safeNumero}${safeData ? "_" + safeData : ""}.xml`;
    */

    a.href = url;
    a.download = fileNameXml;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  if (!xml) return <div>Caricamento fattura…</div>;
  if (!dati?.ok) return <div style={{ color: "red" }}>Errore nel parsing: {dati?.error || "XML non valido"}</div>;

  return (
    <div>
      <div style={{ marginBottom: 8 }}>
        <button onClick={handleDownloadPDF}>Scarica PDF</button>
        &nbsp;
        <button onClick={handleDownloadXML}>Scarica XML</button>
      </div>
      <PDFViewer style={{ width: "100%", height }}>
        <FatturaElettronicaPDFDocument dati={dati} notaInFondo={notaInFondo}/>
      </PDFViewer>
    </div>
  );
}

/* =========================
   parseFattura(xml) -> dati per il PDF
   ========================= */
function parseFattura(xmlText) {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlText, "application/xml");
    const parserError = doc.getElementsByTagName("parsererror")[0];
    if (parserError) throw new Error(parserError.textContent || "XML non valido");

    const root =
      doc.getElementsByTagName("FatturaElettronica")[0] ||
      doc.getElementsByTagName("p:FatturaElettronica")[0] ||
      doc.documentElement;

    const header = firstByName(root, "FatturaElettronicaHeader") || root;
    const datiTrasmissione = firstByName(header, "DatiTrasmissione");
    const idTrasmittente   = datiTrasmissione ? firstByName(datiTrasmissione, "IdTrasmittente") : null;

    const idPaeseTrasmittente   = firstText(idTrasmittente, "IdPaese");
    const idCodiceTrasmittente  = firstText(idTrasmittente, "IdCodice");
    const progressivoInvio      = firstText(datiTrasmissione, "ProgressivoInvio");

    const body = firstByName(root, "FatturaElettronicaBody") || root;

    const datiGenerali = firstByName(body, "DatiGenerali");
    const datiGenDoc = datiGenerali ? firstByName(datiGenerali, "DatiGeneraliDocumento") : null;

    const tipoDocumento = firstText(datiGenDoc, "TipoDocumento");
    const numero = firstText(datiGenDoc, "Numero");
    const dataDoc = firstText(datiGenDoc, "Data");
    const divisa = firstText(datiGenDoc, "Divisa") || "EUR";
    const causale = byName(datiGenDoc || {}, "Causale").map((x) => (x.textContent || "").trim()).join(" • ");

    // Bollo / Ritenuta / Sconti / Arrotondamento
    const datiBollo = datiGenDoc ? firstByName(datiGenDoc, "DatiBollo") : null;
    const bollo = datiBollo ? { bolloVirtuale: firstText(datiBollo, "BolloVirtuale"), importo: toNumber(firstText(datiBollo, "ImportoBollo")) } : null;

    const datiRitenuta = datiGenDoc ? firstByName(datiGenDoc, "DatiRitenuta") : null;
    const ritenuta = datiRitenuta ? {
      tipo: firstText(datiRitenuta, "TipoRitenuta"),
      importo: toNumber(firstText(datiRitenuta, "ImportoRitenuta")),
      aliquota: toNumber(firstText(datiRitenuta, "AliquotaRitenuta")),
      causalePagamento: firstText(datiRitenuta, "CausalePagamento"),
    } : null;

    // Sconti/Magg. a livello documento (li mostriamo in chip/possibile tabella a parte se vuoi)
    // Arrotondamento
    const arrotondamento = toNumber(firstText(datiGenDoc, "Arrotondamento"));

    // Parti
    const cedenteNode = firstByName(header, "CedentePrestatore") || {};
    const cedente = { ...parseAnagrafica(cedenteNode), sede: parseIndirizzo("Sede", cedenteNode) };

    const cessionarioNode = firstByName(header, "CessionarioCommittente") || {};
    const cessionario = { ...parseAnagrafica(cessionarioNode), sede: parseIndirizzo("Sede", cessionarioNode) };

    const terzoIntermediarioNode = firstByName(header, "TerzoIntermediarioOSoggettoEmittente");
    const terzoIntermediario = terzoIntermediarioNode
      ? { ...parseAnagrafica(terzoIntermediarioNode), sede: parseIndirizzo("Sede", terzoIntermediarioNode) }
      : null;
    const soggettoEmittente = firstText(header, "SoggettoEmittente"); // CC/TZ

    // Beni/Servizi + Riepilogo IVA
    const righe = byName(body, "DettaglioLinee").map((r) => {
    const qta = toNumber(firstText(r, "Quantita"), 1);
    const prezzoUnit = toNumber(firstText(r, "PrezzoUnitario"), 0);
    const sconto = byName(r, "ScontoMaggiorazione").reduce((acc, sm) => {
        const t = firstText(sm, "Tipo");
        const imp = toNumber(firstText(sm, "Importo"));
        if (t === "SC") return acc + imp;
        if (t === "MG") return acc - imp;
        return acc;
    }, 0);
    const prezzoTot = toNumber(firstText(r, "PrezzoTotale"), qta * prezzoUnit - sconto);
    const aliquota = toNumber(firstText(r, "AliquotaIVA"));
    const natura = firstText(r, "Natura");
    const codiceArticolo = (() => {
        const ca = firstByName(r, "CodiceArticolo");
        if (!ca) return "";
        const tipo = firstText(ca, "CodiceTipo");
        const val = firstText(ca, "CodiceValore");
        return [tipo, val].filter(Boolean).join(": ");
    })();
    return {
        numeroLinea: firstText(r, "NumeroLinea"),
        descrizione: firstText(r, "Descrizione"),
        quantita: qta,
        unitaMisura: firstText(r, "UnitaMisura"),
        prezzoUnitario: prezzoUnit,
        sconto,
        prezzoTotale: prezzoTot,
        aliquota,
        natura,
        naturaLabel: decodeNatura(natura),
        codiceArticolo,
        ritenutaSi: firstText(r, "Ritenuta") === "SI",
    };
    })
    // Ordino per NumeroLinea (se presente) come intero
    .sort((a, b) => (parseInt(a.numeroLinea || 0, 10) - parseInt(b.numeroLinea || 0, 10)));

    const riepiloghi = byName(body, "DatiRiepilogo").map((rp) => {
    const natura = firstText(rp, "Natura");
    const esig = firstText(rp, "EsigibilitaIVA");
    return {
        aliquota: toNumber(firstText(rp, "AliquotaIVA")),
        natura,
        naturaLabel: decodeNatura(natura),
        imponibile: toNumber(firstText(rp, "ImponibileImporto")),
        imposta: toNumber(firstText(rp, "Imposta")),
        esigibilita: esig,
        esigibilitaLabel: decode(ESIG_MAP, esig),
        riferimentoNormativo: firstText(rp, "RiferimentoNormativo"),
    };
    });

    // Totali
    const imponibileTot = riepiloghi.reduce((s, x) => s + (x.imponibile || 0), 0);
    const impostaTot = riepiloghi.reduce((s, x) => s + (x.imposta || 0), 0);
    const totaleBase = toNumber(firstText(datiGenDoc || {}, "ImportoTotaleDocumento")) || (imponibileTot + impostaTot - arrotondamento);
    const totaleDocumento = totaleBase;

    // Pagamenti
    const pagamentiBlocchi = byName(body, "DatiPagamento").map((dpm) => ({
      condizioni: firstText(dpm, "CondizioniPagamento"),
      dettagli: byName(dpm, "DettaglioPagamento").map((dp) => {
        const mp = firstText(dp, "ModalitaPagamento");
        return {
          modalita: mp, modalitaLabel: decode(MP_MAP, mp),
          dataScadenza: firstText(dp, "DataScadenzaPagamento"),
          importo: toNumber(firstText(dp, "ImportoPagamento")),
          iban: firstText(dp, "IBAN"),
          istituto: firstText(dp, "IstitutoFinanziario"),
          beneficiario: firstText(dp, "Beneficiario"),
          codicePagamento: firstText(dp, "CodicePagamento"),
        };
      }),
    }));

    // Decodifiche “header”
    const tipoDocumentoLabel = decode(TD_MAP, tipoDocumento);
    const soggettoEmittenteLabel = decode(EMITTENTE_MAP, soggettoEmittente);
    const regimeFiscaleLabel = decode(RF_MAP, cedente.regimeFiscale);

    return {
      ok: true,
      error: "",
      header: {
        numero, data: dataDoc, dataIt: fmtDataIt(dataDoc), divisa, causale,
        tipoDocumento, tipoDocumentoLabel, arrotondamento, totaleDocumento,
      },
      idPaeseTrasmittente,
      idCodiceTrasmittente,
      progressivoInvio,
      bollo, ritenuta,
      cedente: { ...cedente, regimeFiscaleLabel },
      cessionario, terzoIntermediario, soggettoEmittente, soggettoEmittenteLabel,
      righe, riepiloghi, pagamentiBlocchi,
    };
  } catch (e) {
    return { ok: false, error: e?.message || String(e) };
  }
}
